import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils';

export const useSiteSearch = (formData, setFormData, appointment) => {
  const [siteSearch, setSiteSearch] = useState(appointment?.site_address || "");
  const [siteSuggestions, setSiteSuggestions] = useState([]);
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const [isSearchingSite, setIsSearchingSite] = useState(false);
  const siteInputRef = useRef(null);
  const siteSearchTimerRef = useRef(null);

  const [nearbyInstallers, setNearbyInstallers] = useState([]);
  const [homeProximityAlternatives, setHomeProximityAlternatives] = useState([]);
  const [checkingNearby, setCheckingNearby] = useState(false);
  const [siteCoordinates, setSiteCoordinates] = useState(null);

  // Sync siteSearch when formData.site_address changes externally (e.g. from Salesforce)
  useEffect(() => {
    if (formData.site_address && formData.site_address !== siteSearch) {
      setSiteSearch(formData.site_address);
    }
  }, [formData.site_address]);

  // Auto-geocode + nearby check whenever address/city/date change (covers ALL sources: SF, DB rubrica, Nominatim, manuale)
  useEffect(() => {
    const addr = formData.site_address || formData.address;
    const city = formData.site_city || formData.city;
    const date = formData.scheduled_date;
    if (!addr || !city || !date) return;

    const timer = setTimeout(async () => {
      // If we already have valid coordinates for this address, reuse them
      if (siteCoordinates && siteCoordinates._for === `${addr}|${city}`) {
        checkNearbyInstallers(siteCoordinates.lat, siteCoordinates.lon, date);
        return;
      }
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr + ', ' + city + ', Italy')}&limit=1`,
          { headers: { 'User-Agent': 'LoJackCalendar/1.0' } }
        );
        const data = await res.json();
        if (data && data.length > 0) {
          const coords = { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon), _for: `${addr}|${city}` };
          setSiteCoordinates(coords);
          checkNearbyInstallers(coords.lat, coords.lon, date);
        }
      } catch (e) {
        console.error('Geocode error:', e);
      }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.site_address, formData.site_city, formData.address, formData.city, formData.scheduled_date,
      formData.scheduled_time, formData.assigned_installer_id]);

  const searchSiteAddress = async (query) => {
    if (query.length < 3) {
      setSiteSuggestions([]);
      return;
    }
    setIsSearchingSite(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ", Italy")}&limit=5&addressdetails=1`,
        { headers: { "User-Agent": "LoJackCalendar/1.0" } }
      );
      const data = await response.json();
      setSiteSuggestions(data.map(item => ({
        display_name: item.display_name,
        address: item.address?.road ? `${item.address.road}${item.address.house_number ? ' ' + item.address.house_number : ''}` : item.name || "",
        city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || "",
        province: item.address?.county || item.address?.state || "",
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon)
      })));
    } catch (error) {
      console.error("Error searching site:", error);
      setSiteSuggestions([]);
    } finally {
      setIsSearchingSite(false);
    }
  };

  const handleSiteSearchChange = (value) => {
    setSiteSearch(value);
    setFormData(prev => ({ ...prev, site_address: value, address: value }));
    if (siteSearchTimerRef.current) clearTimeout(siteSearchTimerRef.current);
    siteSearchTimerRef.current = setTimeout(() => {
      searchSiteAddress(value);
      setShowSiteDropdown(true);
    }, 300);
  };

  const selectSite = (site) => {
    setSiteSearch(site.address || site.display_name.split(",")[0]);
    setFormData(prev => ({
      ...prev,
      site_address: site.address || site.display_name.split(",")[0],
      site_city: site.city,
      site_province: site.province.substring(0, 2).toUpperCase(),
      address: site.address || site.display_name.split(",")[0],
      city: site.city,
      province: site.province.substring(0, 2).toUpperCase()
    }));
    setSiteCoordinates({ lat: site.lat, lon: site.lon });
    setShowSiteDropdown(false);
    setSiteSuggestions([]);
    if (formData.scheduled_date && site.lat && site.lon) {
      checkNearbyInstallers(site.lat, site.lon, formData.scheduled_date);
    }
  };

  const checkNearbyInstallers = async (lat, lon, date) => {
    if (!lat || !lon || !date) return;
    setCheckingNearby(true);
    try {
      const response = await axios.post(`${API}/appointments/check-nearby-installers`, {
        latitude: lat, longitude: lon, date: date,
        exclude_appointment_id: appointment?.id || null,
        scheduled_time: formData.scheduled_time || null,
        selected_installer_id: formData.assigned_installer_id || null,
      });
      setNearbyInstallers(response.data.has_nearby ? response.data.installers : []);
      setHomeProximityAlternatives(response.data.home_proximity_alternatives || []);
    } catch (error) {
      console.error("Error checking nearby installers:", error);
      setNearbyInstallers([]);
      setHomeProximityAlternatives([]);
    } finally {
      setCheckingNearby(false);
    }
  };

  return {
    siteSearch, setSiteSearch, siteSuggestions, showSiteDropdown, setShowSiteDropdown,
    isSearchingSite, siteInputRef,
    nearbyInstallers, homeProximityAlternatives, checkingNearby,
    handleSiteSearchChange, selectSite,
  };
};
